/**
 * Tabs component — shared types and Zod prop schema.
 *
 * An underline tab bar: a horizontal row of text tabs where the active tab
 * shows bold primary text over a rounded underline and the row sits on a
 * hairline divider. One coherent visual style — no variant enum. The row is
 * always overflow-capable: item sets wider than the available width scroll
 * horizontally, fitting sets simply do not scroll. Labels arrive as consumer-supplied strings, so localization stays
 * with the consumer and the element owns no copy.
 */

import { z } from 'zod';
import { actionIdSchema } from '@galapagos/product-analytics';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';

export const TABS_UNDERLINE_HEIGHT = sizing.stroke.emphasis;

/** Web analog of the native hairline divider. */
export const TABS_WEB_DIVIDER_HEIGHT = sizing.stroke.hairline;

/** Touch-area extension so a compact tab (short label, no horizontal padding) reaches the 44pt minimum in both axes; the row's inter-tab gap exceeds the combined horizontal slop, so targets never overlap. */
export const TABS_TAB_HIT_SLOP = {
  top: spacing.s,
  bottom: spacing.s,
  left: spacing.s,
  right: spacing.s,
} as const;

export const tabsItemSchema = z.object({
  /** Stable kebab-case key; doubles as the analytics `element_key`. */
  key: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Expected a stable kebab-case key'),
  label: z.string().min(1),
  /** Overrides the derived `{tabsTestID}-{key}` per-item testID (e.g. to keep
   * a pre-existing identifier that e2e selectors already pin). */
  testID: z.string().optional(),
});

export type TabsItem = z.infer<typeof tabsItemSchema>;

export const tabsPropsSchema = z.object({
  items: z.array(tabsItemSchema).min(1),
  /** Key of the active item; pressing the active tab is a no-op. */
  activeKey: z.string().min(1),
  /**
   * Required, consumer-supplied action id — one per tab bar, with the item key
   * riding along as `element_key`. No generic default: a tab bar without a
   * semantic id would pollute the action catalogue.
   */
  actionId: actionIdSchema,
  /** Names the tab list group for screen readers. */
  accessibilityLabel: z.string().optional(),
  testID: z.string().optional(),
});

export type TabsProps = z.input<typeof tabsPropsSchema>;

function requireUniqueItemKeys(value: { items: TabsItem[] }, context: z.RefinementCtx): void {
  const seen = new Set<string>();
  for (const item of value.items) {
    if (seen.has(item.key)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items'],
        message: `Duplicate tab key "${item.key}"`,
      });
    }
    seen.add(item.key);
  }
}

/**
 * Runtime schema with cross-field rules the flat catalog schema can't carry
 * (ADR-011 Rule 5 keeps refinements out of the JSON-schema export): item keys
 * must be unique (they drive React keys and analytics element keys) and an
 * authored `activeKey` must reference an item.
 */
export const tabsPropsRefinedSchema = tabsPropsSchema.superRefine((value, context) => {
  requireUniqueItemKeys(value, context);
  if (!value.items.some((item) => item.key === value.activeKey)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['activeKey'],
      message: `activeKey "${value.activeKey}" does not match any item key`,
    });
  }
});

/**
 * The unique-keys half of the refined contract, for generated specs whose
 * `activeKey` is runtime-bound (the bound key is removed before validation,
 * so the membership rule has nothing authored to check).
 */
export const tabsPropsWithBoundActiveKeySchema = tabsPropsSchema
  .omit({ activeKey: true })
  .superRefine(requireUniqueItemKeys);

/** Per-item testID: `{tabsTestID}-{itemKey}`, matching the tab-bar convention. */
export function tabsItemTestID(testID: string | undefined, key: string): string | undefined {
  return testID ? `${testID}-${key}` : undefined;
}
